import React, { useState } from "react"
import { EWsrvTransform, IMediaItem } from "../../lib";
import { ILinkFunc } from "../../lib/util";
import { Button } from "../Button";
import { WsrvImage } from "../Media";
import Heading from "./Heading";
import { TabbedContentBlade, ITabbedContentBladeProps, ITabContent } from "./TabbedContentBlade";

export interface IFeaturedContentTab extends Omit<ITabContent, 'content'> {
    content: string;
    link?: { label: string; url: string };
    image: IMediaItem;
}

export interface ITabbedSectionWithImage extends Omit<ITabbedContentBladeProps, 'tabs'> {
    tabs: IFeaturedContentTab[];
}

export interface IFeaturedWithTabsContentProps {
    className: string;
    linkFunc?: ILinkFunc;
    title?: string;
    titleHeadingLevel?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p";
    subtitle?: string;
    tabbedContent: ITabbedSectionWithImage;
}

export const FeaturedWithTabsContent: React.FC<IFeaturedWithTabsContentProps> = ({
    className = "",
    linkFunc,
    title,
    titleHeadingLevel = "h3",
    subtitle,
    tabbedContent,
}) => {
    const linkf = linkFunc || ((props) => <a {...props} />);

    const [currentImage, setCurrentImage] = useState(tabbedContent.tabs[0].image)

    const tabChangeHandler = (tabIndex?: any) => {
        let tabImage = tabbedContent.tabs[tabIndex]?.image
        setCurrentImage(tabImage);
    }

    const tabItems: ITabContent[] = tabbedContent.tabs.map((item: IFeaturedContentTab) => {
        return {
            tabId: item.tabId,
            tabTitle: item.tabTitle,
            contentClassName: item.contentClassName,
            contentId: item.contentId,
            image: item.image,
            content: (
                <>
                    <div dangerouslySetInnerHTML={{ __html: item.content }}></div>
                    {item.link && (
                        <Button
                            line={true}
                            icon={false}
                            linkProps={{
                                href: item.link.url,
                                className: `normal-case italic font-normal mt-6`,
                            }}
                            linkFunc={linkf}
                        >
                            {item.link.label}
                        </Button>
                    )}
                </>
            )
        }
    })

    const featuredContentTabs: ITabbedContentBladeProps = {
        ...tabbedContent,
        tabs: tabItems,
    }

    return (
        <section className={`featured-tabbed-content flex flex-row flex-wrap w-full ${className}`}>
            <div className="flex flex-col justify-center w-full lg:w-1/2 lg:py-24 lg:border-r-2">
                <Heading className="text-left" headingLevel={titleHeadingLevel}>{title}</Heading>
                {subtitle && (
                    <div className="flex flex-row flex-nowrap items-center italic">
                        <div className="w-2/12 lg:w-1/12 border-t border-solid"></div>
                        <span className="pl-3 text-xl">{subtitle}</span>
                    </div>
                )}
                <WsrvImage
                    urlParams={{
                        originURL: currentImage.url,
                        preset: EWsrvTransform.cover3x2,
                    }}
                    imgProps={{
                        className: "max-w-full pt-4 lg:py-8",
                    }} />
            </div>
            <div className="flex flex-col justify-center w-full lg:w-1/2 lg:pl-12">
                <TabbedContentBlade
                    onTabChange={tabChangeHandler}
                    {...featuredContentTabs}
                />
            </div>
        </section>
    )
}

export default FeaturedWithTabsContent